import { clsx } from 'clsx';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Occupe toute la largeur (segments d'égale taille). */
  block?: boolean;
  /** Autorise le retour à la ligne (nombreuses options courtes). */
  wrap?: boolean;
  ariaLabel?: string;
  className?: string;
}

/**
 * Choix mutuellement exclusif — remplaçant unique des groupes de radios ronds.
 *
 * L'option active est peinte à l'accent : la SÉLECTION EST LA COULEUR, langage
 * commun avec `OptionToggle`. Un vrai groupe `role="radiogroup"` avec des
 * boutons `aria-checked` : sémantique de radio préservée pour les lecteurs
 * d'écran, sans le rendu natif du bouton radio.
 *
 * Pour des options riches (titre + description), utiliser plutôt `OptionToggle`
 * en variante `card` : le segmented vise les libellés courts.
 */
export default function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  block = false,
  wrap = false,
  ariaLabel,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={clsx('segmented', block && 'segmented--block', wrap && 'segmented--wrap', className)}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          disabled={opt.disabled}
          onClick={() => onChange(opt.value)}
          className={clsx(value === opt.value && 'on')}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  );
}
